import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../environments/environment';
import { SystemSettingsDTO } from './services/system-settings.service';

export interface User {
  id: string;
  tenantId: string | null;  // null si es Platform Admin (Pezeta Solutions)
  /** Slug del tenant, para volver a su login con marca al cerrar sesión. Null para Platform Admin. */
  tenantSlug: string | null;
  email: string;
  role: string;
  name: string;
  permissions: {
    manageUsers: boolean;
    manageRoles: boolean;
    manageDepartments: boolean;
    managePositions: boolean;
    manageEmployees: boolean;
    manageGallery: boolean;
    manageCrm: boolean;
    manageChatbot: boolean;
    manageInventory: boolean;
  };
}

export interface AuthResponse {
  token: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = environment.apiUrl;
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  private jwtHelper = new JwtHelperService();

  constructor(
    private http: HttpClient,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.checkToken();
  }

  private checkToken(): void {
    if (isPlatformBrowser(this.platformId)) {
      const token = localStorage.getItem('token');
      if (token && !this.jwtHelper.isTokenExpired(token)) {
        this.setUserFromToken(token);
      } else {
        // Solo limpiar el token sin redirigir automáticamente
        localStorage.removeItem('token');
        this.currentUserSubject.next(null);
      }
    }
  }

  login(email: string, password: string): Observable<User> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/account/login`, { email, password })
      .pipe(
        tap(response => {
          // Asegúrate de que el token existe antes de guardarlo
          if (response && response.token && isPlatformBrowser(this.platformId)) {
            localStorage.setItem('token', response.token);
            this.setUserFromToken(response.token);
            // Añade un log para verificar
            console.log('Token almacenado y usuario configurado', this.currentUserSubject.value);
          }
        }),
        map(() => {
          if (!this.currentUserSubject.value) {
            throw new Error('Error al procesar el token');
          }
          return this.currentUserSubject.value;
        }),
        catchError(error => {
          // Manejo específico para error 401
          if (error.status === 401) {
            return throwError(() => new Error('Credenciales inválidas'));
          }
          return throwError(() => new Error(error.error?.message || 'Error en el inicio de sesión'));
        })
      );
  }

  logout(redirectToLogin: boolean = true): void {
    // Se resuelve ANTES de limpiar la sesión: después ya no hay de dónde sacar el tenant.
    const tenantSlug = this.resolveTenantSlugForLogout();

    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('token');
    }
    this.currentUserSubject.next(null);

    if (redirectToLogin) {
      // Al login con la marca de su empresa; el genérico queda para Platform Admin y para tokens
      // emitidos antes de que existiera el claim, que no traen slug.
      this.router.navigate(tenantSlug ? ['/login', tenantSlug] : ['/login']);
    }
  }

  /**
   * Slug del tenant de la sesión que se está cerrando. Normalmente sale del usuario en memoria,
   * pero cuando el cierre lo dispara un 401 tras recargar la página el sujeto puede estar vacío,
   * así que se recurre al token guardado: aunque esté expirado, sus claims se siguen leyendo y son
   * suficientes para saber a qué login devolver al usuario.
   */
  private resolveTenantSlugForLogout(): string | null {
    const fromMemory = this.currentUserSubject.value?.tenantSlug;
    if (fromMemory) {
      return fromMemory;
    }

    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      const token = localStorage.getItem('token');
      return token ? this.jwtHelper.decodeToken(token)?.tenant_slug ?? null : null;
    } catch {
      return null;
    }
  }

  getToken(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem('token');
    }
    return null;
  }

  isLoggedIn(): boolean {
    console.log('Verificando si el usuario está autenticado');
    const token = this.getToken();
    return token !== null && !this.jwtHelper.isTokenExpired(token);
  }

  private setUserFromToken(token: string): void {
    try {
      const decodedToken = this.jwtHelper.decodeToken(token);
      const user: User = {
        id: decodedToken.id,
        tenantId: decodedToken.tenant_id || null,  // null si es Platform Admin
        tenantSlug: decodedToken.tenant_slug || null,
        email: decodedToken.email,
        role: decodedToken.role,
        name: decodedToken.fullName,
        permissions: {
          manageUsers: decodedToken.manage_users === 'true',
          manageRoles: decodedToken.manage_roles === 'true',
          manageDepartments: decodedToken.manage_departments === 'true',
          managePositions: decodedToken.manage_positions === 'true',
          manageEmployees: decodedToken.manage_employees === 'true',
          manageGallery: decodedToken.manage_gallery === 'true',
          manageCrm: decodedToken.manage_crm === 'true',
          manageChatbot: decodedToken.manage_chatbot === 'true',
          manageInventory: decodedToken.manage_inventory === 'true',
        }
      };
      this.currentUserSubject.next(user);
    } catch (error) {
      console.error('Error decodificando el token', error);
      // Solo limpiar credenciales sin redirigir automáticamente
      localStorage.removeItem('token');
      this.currentUserSubject.next(null);
    }
  }

  hasPermission(permission: keyof User['permissions']): boolean {
    const user = this.currentUserSubject.value;
    return user?.permissions[permission] || false;
  }

  isAdmin(): boolean {
    return this.currentUserSubject.value?.role === 'Admin';
  }

  isPlatformAdmin(): boolean {
    const user = this.currentUserSubject.value;
    return user !== null && user.tenantId === null;
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  getCurrentUserEmail(): string {
    const token = this.getToken();
    if (token && !this.jwtHelper.isTokenExpired(token)) {
      try {
        const decodedToken = this.jwtHelper.decodeToken(token);
        return decodedToken.email || 'Usuario CRM';
      } catch (error) {
        console.error('Error decodificando el token', error);
        return 'Usuario CRM';
      }
    }
    return 'Usuario CRM';
  }

  getCurrentUserId(): string {
    const token = this.getToken();
    if (token && !this.jwtHelper.isTokenExpired(token)) {
      try {
        const decodedToken = this.jwtHelper.decodeToken(token);
        return decodedToken.sub || decodedToken.id || '';
      } catch (error) {
        console.error('Error decodificando el token', error);
        return '';
      }
    }
    return '';
  }

  getCurrentUserFullName(): string {
    const token = this.getToken();
    if (token && !this.jwtHelper.isTokenExpired(token)) {
      try {
        const decodedToken = this.jwtHelper.decodeToken(token);

        // Verificar diferentes posibles nombres para el claim
        const fullName = decodedToken.fullName ||
          decodedToken.FullName ||
          decodedToken.full_name ||
          decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] ||
          decodedToken.name;

        if (fullName) {
          return fullName;
        }

        // Si no hay fullName, usar el email pero sin el dominio
        if (decodedToken.email) {
          const emailPart = decodedToken.email.split('@')[0];
          // Capitalizar la primera letra y reemplazar puntos/guiones por espacios
          return emailPart.charAt(0).toUpperCase() + emailPart.slice(1).replace(/[._-]/g, ' ');
        }

        return 'Usuario CRM';
      } catch (error) {
        console.error('Error decodificando el token', error);
        return 'Usuario CRM';
      }
    }
    return 'Usuario CRM';
  }

 private settings$ = new BehaviorSubject<SystemSettingsDTO[]>([]);

  get settings(): Observable<SystemSettingsDTO[]> {
    return this.settings$.asObservable();
  }

  setSettings(settings: SystemSettingsDTO[]) {
    this.settings$.next(settings);
  }

  isEnabled(key: string): boolean {
    const s = this.settings$.value.find(x => x.key === key);
    return s?.value === 'true';
  }

  forgotPassword(email: string): Observable<{message: string}> {
    return this.http.post<{message: string}>(`${this.apiUrl}/account/forgot-password`, { email })
      .pipe(
        catchError(error => {
          if (error.status === 400) {
            return throwError(() => new Error('Email inválido'));
          }
          return throwError(() => new Error(error.error?.message || 'Error al enviar el email de recuperación'));
        })
      );
  }

  resetPassword(email: string, token: string, newPassword: string, confirmPassword: string): Observable<{message: string}> {
    return this.http.post<{message: string}>(`${this.apiUrl}/account/reset-password`, {
      email,
      token,
      newPassword,
      confirmPassword
    })
    .pipe(
      catchError(error => {
        if (error.status === 400) {
          const errorMessage = error.error?.errors?.[0]?.description || 'Datos inválidos';
          return throwError(() => new Error(errorMessage));
        }
        return throwError(() => new Error(error.error?.message || 'Error al restablecer la contraseña'));
      })
    );
  }
}